import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Button,
  Collapse,
  alpha,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import SmartCardChart from './SmartCardChart';
import SummaryPublicationCard from './SummaryPublicationCard';
import ActionableCard from './ActionableCard';
import ReminderCard from './ReminderCard';
import NewsCard from './NewsCard';
import { useSubscription } from './SubscriptionContext';
import { getButtonTarget, goToButtonTarget } from './publicationHandlers';
// LAP-733 (GE022) round 10 — pull the active UI language so the date locale
// and the "Voir" fallback action label follow the user's choice.
import { useSelector } from 'react-redux';
import { getTranslation, getUserLanguage } from './translations';

const SmartCard = ({ publication, onInteraction, onUpgradeRequest, showArchivedIndicators = false }) => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [expanded, setExpanded] = useState(false);
  // LAP-733 (GE022) round 10 — resolve the active language and derive a date
  // locale string (e.g. fr → fr-FR). Falls back to the bare language code if
  // the mapping is missing, which Intl.DateTimeFormat still understands.
  const siteUser = useSelector((store) => store.user);
  const lang = getUserLanguage(siteUser);
  // LAP-733 (GE022) round 14 — extend date locale map to zh/ja/ar.
  const dateLocale = ({ fr: 'fr-FR', en: 'en-GB', de: 'de-DE', it: 'it-IT', es: 'es-ES', zh: 'zh-CN', ja: 'ja-JP', ar: 'ar-SA' })[lang] || lang || 'fr-FR';
  // LAP-733 (GE022) round 14 — translate the publication type chip label
  // so the SmartCard pill follows the active UI language instead of always
  // showing the raw English type from the backend.
  const TYPE_LABELS = {
    fr: { Motivational: 'Motivation', News: 'Actualité', Summary: 'Résumé', Actionable: 'À faire', Reminder: 'Rappel', Publication: 'Publication' },
    en: { Motivational: 'Motivational', News: 'News', Summary: 'Summary', Actionable: 'Action', Reminder: 'Reminder', Publication: 'Publication' },
    de: { Motivational: 'Motivation', News: 'Nachrichten', Summary: 'Zusammenfassung', Actionable: 'Aktion', Reminder: 'Erinnerung', Publication: 'Beitrag' },
    it: { Motivational: 'Motivazione', News: 'Notizie', Summary: 'Riepilogo', Actionable: 'Azione', Reminder: 'Promemoria', Publication: 'Pubblicazione' },
    es: { Motivational: 'Motivación', News: 'Noticias', Summary: 'Resumen', Actionable: 'Acción', Reminder: 'Recordatorio', Publication: 'Publicación' },
    zh: { Motivational: '激励', News: '资讯', Summary: '摘要', Actionable: '行动', Reminder: '提醒', Publication: '动态' },
    ja: { Motivational: 'モチベーション', News: 'ニュース', Summary: '要約', Actionable: 'アクション', Reminder: 'リマインダー', Publication: '投稿' },
    ar: { Motivational: 'تحفيز', News: 'أخبار', Summary: 'ملخص', Actionable: 'إجراء', Reminder: 'تذكير', Publication: 'منشور' },
  };

  let enhancedPublication = { ...publication };

  // Map old "Insight" type to "Publication" for display
  if (enhancedPublication.type === 'Insight') {
    enhancedPublication.type = 'Publication';
  }

  // LAP-733 (GE022) round 14 — compute typeLabel AFTER enhancedPublication is
  // fully resolved (Insight → Publication mapping applied). Was previously
  // declared before `let enhancedPublication`, which caused a TDZ ReferenceError
  // "Cannot access 'g' before initialization" in the minified bundle.
  const typeLabel = (TYPE_LABELS[lang] && TYPE_LABELS[lang][enhancedPublication.type]) || enhancedPublication.type;
  
  const definePublicationId = enhancedPublication.define_publication?.id || enhancedPublication.define_publication_id;
  
  // LAP — deep-link entity binding : le button_target renvoyé par le backend
  // pour CETTE instance (ex: /mesobjectifs?focus=42) PRIME sur la table de
  // routage statique. Avant, getButtonTarget() écrasait systématiquement le
  // lien profond porté par l'instance, ramenant l'utilisateur sur la liste
  // générique sans savoir de quelle entité il s'agissait. On ne retombe donc
  // sur la table statique QUE si le backend n'a pas fourni de cible.
  if (!enhancedPublication.button_target && definePublicationId) {
    const target = getButtonTarget(definePublicationId, enhancedPublication.domain, enhancedPublication.type);
    if (target) {
      enhancedPublication.button_target = target;
    } else {
      // Motivational and News publications (e.g. IDs 272-288) intentionally
      // have no route mapping — they're rendered as static cards without a
      // CTA target. Skip the warning for those types to avoid console noise.
      const typesWithoutRoute = ['Motivational', 'News'];
      if (!typesWithoutRoute.includes(enhancedPublication.type)) {
        console.warn('No route mapping found for publication ID:', definePublicationId, 'domain:', enhancedPublication.domain, 'type:', enhancedPublication.type);
      }
    }
  }
  
  // LAP — deep-link entity binding : quand la carte cible une entité précise
  // (objectif, tâche, document…), on affiche SON NOM dans le sous-titre pour
  // lever l'ambiguïté « de laquelle parle-t-on ? ». Indépendant de la langue
  // (le nom vient des données de l'utilisateur) et générique à tous les types
  // de carte, qui rendent tous `subtitle`.
  const ec = enhancedPublication.entity_context;
  if (ec && ec.label) {
    const datePart = ec.date ? ` — ${ec.date}` : '';
    const focusLine = `${ec.label}${datePart}`;
    const existingSubtitle = enhancedPublication.subtitle || '';
    if (!existingSubtitle.includes(ec.label)) {
      enhancedPublication.subtitle = existingSubtitle
        ? `${focusLine} · ${existingSubtitle}`
        : focusLine;
    }
  }

  if (enhancedPublication.type === 'Summary') {
    return <SummaryPublicationCard publication={enhancedPublication} onInteraction={onInteraction} showArchivedIndicators={showArchivedIndicators} />;
  }

  if (enhancedPublication.type === 'Actionable') {
    return <ActionableCard publication={enhancedPublication} onInteraction={onInteraction} onUpgradeRequest={onUpgradeRequest} showArchivedIndicators={showArchivedIndicators} />;
  }

  if (enhancedPublication.type === 'Reminder') {
    return <ReminderCard publication={enhancedPublication} onInteraction={onInteraction} showArchivedIndicators={showArchivedIndicators} />;
  }

  if (enhancedPublication.type === 'News') {
    return <NewsCard publication={enhancedPublication} onInteraction={onInteraction} showArchivedIndicators={showArchivedIndicators} />;
  }

  // Special rendering for Publication type
  if (enhancedPublication.type === 'Publication') {
    const handleDismissPublication = (e) => {
      e.stopPropagation();
      onInteraction(publication.id, 'dismiss');
    };

    const handleLikePublication = (e) => {
      e.stopPropagation();
      onInteraction(publication.id, 'like');
    };

    const handleSavePublication = (e) => {
      e.stopPropagation();
      onInteraction(publication.id, 'save');
    };

    const publicationGradient = 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
    const publicationColor = '#8b5cf6';

    // LAP — bouton d'action manquant : ce bloc "Publication" n'avait jamais
    // porté de CTA (contrairement à Motivational/Reminder/News/Actionable),
    // alors que le serializer renvoie bien button_text/button_target quand le
    // template les définit (ex: cartes "Le mot du conseiller…" → /conseiller-*).
    // Sans ce bouton la carte était un cul-de-sac : aucun moyen d'atteindre
    // le chat auto-rempli (goToButtonTarget + seedPrompt).
    const actionTargetPublication = enhancedPublication.button_target
      || getButtonTarget(definePublicationId, enhancedPublication.domain, enhancedPublication.type);
    const hasActionButtonPublication = (enhancedPublication.button_text || enhancedPublication.button_target)
      && !!actionTargetPublication;
    const handleActionPublication = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      goToButtonTarget(actionTargetPublication, navigate, enhancedPublication);
      onInteraction(enhancedPublication.id, 'click');
    };

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#1a1d2e',
          borderRadius: '16px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 25px -5px ${alpha(publicationColor, 0.4)}, 0 10px 10px -5px ${alpha(
              publicationColor,
              0.3,
            )}`,
            borderColor: alpha(publicationColor, 0.5),
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: publicationGradient,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="start" gap={1} flexGrow={1}>
              <Typography
                variant="h4"
                component="span"
                sx={{
                  fontSize: '2rem',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                }}
              >
                {enhancedPublication.icon || '💡'}
              </Typography>

              <Box flexGrow={1}>
                {/* Title */}
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    lineHeight: 1.4,
                    color: '#E0E0E0',
                    mb: 0.5,
                  }}
                >
                  {enhancedPublication.title}
                </Typography>

                {/* Subtitle */}
                {enhancedPublication.subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha('#E0E0E0', 0.7),
                      fontSize: '0.85rem',
                      mb: 1.5,
                    }}
                  >
                    {enhancedPublication.subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={handleDismissPublication}
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  color: '#ef4444',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Text Content (text1) */}
          {enhancedPublication.text1 && (
            <Typography
              variant="body2"
              sx={{
                color: alpha('#E0E0E0', 0.9),
                fontSize: '0.9rem',
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              {enhancedPublication.text1}
            </Typography>
          )}

          {/* Progress Bar / Graph */}
          {enhancedPublication.graph_config && (
            <Box sx={{ mb: 2 }}>
              <SmartCardChart config={enhancedPublication.graph_config} color={publicationColor} domain={enhancedPublication.domain} />
            </Box>
          )}

          {/* Image */}
          {enhancedPublication.image_url && (
            <Box
              sx={{
                width: '100%',
                borderRadius: '12px',
                overflow: 'hidden',
                mb: 2,
              }}
            >
              <img
                src={enhancedPublication.image_url}
                alt={enhancedPublication.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}

          {/* Secondary Text (text2) */}
          {enhancedPublication.text2 && (
            <Typography
              variant="body2"
              sx={{
                color: alpha('#E0E0E0', 0.7),
                fontSize: '0.85rem',
                lineHeight: 1.6,
                fontStyle: 'italic',
                mb: hasActionButtonPublication ? 2 : 0,
              }}
            >
              {enhancedPublication.text2}
            </Typography>
          )}

          {/* Action button only when there is a valid target */}
          {hasActionButtonPublication && (
            <Button
              variant="contained"
              fullWidth
              onClick={handleActionPublication}
              sx={{
                background: publicationGradient,
                color: '#fff',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                py: 1,
                fontSize: '0.9rem',
                boxShadow: `0 4px 12px ${alpha(publicationColor, 0.35)}`,
                '&:hover': {
                  background: publicationGradient,
                  boxShadow: `0 6px 16px ${alpha(publicationColor, 0.5)}`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {enhancedPublication.button_text || getTranslation('viewAction', lang) /* LAP-733 (GE022) round 10 */}
            </Button>
          )}
        </CardContent>

        {/* Footer Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            bgcolor: 'rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box display="flex" gap={0.5}>
            <IconButton
              size="small"
              onClick={handleLikePublication}
              sx={{
                color: enhancedPublication.was_liked ? '#ef4444' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  color: '#ef4444',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              {enhancedPublication.was_liked ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSavePublication}
              sx={{
                color: enhancedPublication.is_saved ? publicationColor : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  color: publicationColor,
                  bgcolor: alpha(publicationColor, 0.1),
                },
              }}
            >
              {enhancedPublication.is_saved ? (
                <BookmarkIcon fontSize="small" />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={typeLabel /* LAP-733 (GE022) round 14 */}
              size="small"
              sx={{
                background: publicationGradient,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.7rem',
              }}
            >
              {new Date(enhancedPublication.datetime_displayed).toLocaleDateString(dateLocale, { /* LAP-733 (GE022) round 10 */
                month: 'short',
                day: 'numeric',
              })}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  }

  // Special rendering for Motivational and Reminder types
  if (enhancedPublication.type === 'Motivational' || enhancedPublication.type === 'Reminder') {
    const handleDismissSpecial = (e) => {
      e.stopPropagation();
      onInteraction(publication.id, 'dismiss');
    };

    const handleLikeSpecial = (e) => {
      e.stopPropagation();
      onInteraction(publication.id, 'like');
    };

    const handleSaveSpecial = (e) => {
      e.stopPropagation();
      onInteraction(publication.id, 'save');
    };

    const actionTargetSpecial = enhancedPublication.button_target || getButtonTarget(definePublicationId, enhancedPublication.domain, enhancedPublication.type);
    const hasActionButtonSpecial = (enhancedPublication.button_text || enhancedPublication.button_target) && !!actionTargetSpecial;
    const handleActionSpecial = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      goToButtonTarget(actionTargetSpecial, navigate, enhancedPublication);
      onInteraction(enhancedPublication.id, 'click');
    };

    // Define gradient and shadow based on type
    const isReminder = enhancedPublication.type === 'Reminder';
    const gradient = isReminder
      ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #f87171 100%)'
      : 'linear-gradient(180deg, #6B0F9C 0%, #C024B6 50%, #E73C7E 100%)';
    const shadowColor = isReminder ? 'rgba(220, 38, 38, 0.4)' : 'rgba(107, 15, 156, 0.4)';
    const hoverShadowColor = isReminder ? 'rgba(220, 38, 38, 0.6)' : 'rgba(107, 15, 156, 0.6)';

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: gradient,
          borderRadius: '16px',
          border: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `0 10px 30px ${shadowColor}`,
          '&:hover': {
            transform: 'translateY(-4px) scale(1.02)',
            boxShadow: `0 20px 40px ${hoverShadowColor}`,
          },
        }}
      >
        <CardContent
          sx={{
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          {/* Close Button */}
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton
              size="small"
              onClick={handleDismissSpecial}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  color: '#fff',
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Icon */}
          <Typography
            variant="h2"
            component="span"
            sx={{
              fontSize: '4rem',
              mb: 2,
              filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))',
            }}
            >
              {enhancedPublication.icon || '✨'}
            </Typography>

          {/* Image */}
          {enhancedPublication.image_url && (
            <Box
              sx={{
                width: '100%',
                maxWidth: '400px',
                borderRadius: '12px',
                overflow: 'hidden',
                mb: 2,
                boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
              }}
            >
              <img
                src={enhancedPublication.image_url}
                alt={enhancedPublication.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'cover',
                  maxHeight: '300px',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}

            {/* Title */}
            <Typography
              variant="h5"
              component="h3"
              sx={{
                fontWeight: 800,
                fontSize: '1.5rem',
                lineHeight: 1.3,
                mb: 2,
                color: '#fff',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              {enhancedPublication.title}
            </Typography>

            {/* OpenAI Generated Text (text1) */}
            {enhancedPublication.text1 && (
              <Typography
                variant="body1"
                sx={{
                  color: '#fff',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  fontWeight: 500,
                  textShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  maxWidth: '90%',
                  mb: (enhancedPublication.button_text || enhancedPublication.button_target) ? 2 : 0,
                }}
              >
                {enhancedPublication.text1}
              </Typography>
            )}

            {/* Action button only when there is a valid target */}
            {hasActionButtonSpecial && (
              <Button
                variant="contained"
                onClick={handleActionSpecial}
                sx={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  color: '#fff',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.35)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)',
                    transform: 'translateY(-2px)',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {enhancedPublication.button_text || getTranslation('viewAction', lang) /* LAP-733 (GE022) round 10 */}
              </Button>
            )}
        </CardContent>

        {/* Footer Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box display="flex" gap={0.5}>
            <IconButton
              size="small"
              onClick={handleLikeSpecial}
              sx={{
                color: enhancedPublication.was_liked ? '#fff' : 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  color: '#fff',
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {enhancedPublication.was_liked ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSaveSpecial}
              sx={{
                color: enhancedPublication.is_saved ? '#fff' : 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  color: '#fff',
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {enhancedPublication.is_saved ? (
                <BookmarkIcon fontSize="small" />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.7rem',
              fontWeight: 600,
            }}
          >
            {new Date(enhancedPublication.datetime_displayed).toLocaleDateString(dateLocale, { /* LAP-733 (GE022) round 10 */
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        </Box>
      </Card>
    )
  }

  const handleLike = (e) => {
    e.stopPropagation();
    onInteraction(enhancedPublication.id, 'like');
  };

  const handleSave = (e) => {
    e.stopPropagation();
    onInteraction(enhancedPublication.id, 'save');
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    onInteraction(enhancedPublication.id, 'dismiss');
  };

  const handleAction = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    goToButtonTarget(enhancedPublication.button_target, navigate, enhancedPublication);
    onInteraction(enhancedPublication.id, 'click');
  };
  const hasActionButton = (enhancedPublication.button_text || enhancedPublication.button_target) && !!enhancedPublication.button_target;

  const getDomainColor = (domain) => {
    const colors = {
      Finance: '#10b981',
      Social: '#ec4899',
      Health: '#ef4444',
      Professional: '#3b82f6',
      Personal: '#8b5cf6',
      Time: '#f59e0b',
      Habitation: '#06b6d4',
      Home: '#06b6d4',
      Planet: '#06b6d4',
      Administrative: '#6366f1',
      Roadmap: '#9c27b0',
      General: '#6366f1',
    };
    return colors[domain] || '#6366f1';
  };

  const getTypeGradient = (type) => {
    const gradients = {
      Actionable: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      Summary: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      Reminder: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      Publication: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      Motivational: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      Custom: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      News: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    };
    return gradients[type] || gradients.Custom;
  };

  const domainColor = getDomainColor(enhancedPublication.domain);
  const typeGradient = getTypeGradient(enhancedPublication.type);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1a1d2e',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 25px -5px ${alpha(domainColor, 0.3)}, 0 10px 10px -5px ${alpha(
            domainColor,
            0.2,
          )}`,
          borderColor: alpha(domainColor, 0.5),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: typeGradient,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="start" gap={1}>
            <Typography
              variant="h4"
              component="span"
              sx={{
                fontSize: '2rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            >
              {enhancedPublication.icon || '📋'}
            </Typography>

            <Box>
              {/* Title */}
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.4,
                  // mb: 1,
                  color: '#E0E0E0',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textAlign: 'left',
                }}
              >
                {enhancedPublication.title}
              </Typography>

              {/* Subtitle */}
              {enhancedPublication.subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#E0E0E0', 0.7),
                    fontSize: '0.85rem',
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {enhancedPublication.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                color: '#ef4444',
                bgcolor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Domain Badge */}
        {/* <Box mb={2}>
          <Chip
            label={publication.domain}
            size="small"
            sx={{
              bgcolor: alpha(domainColor, 0.15),
              color: domainColor,
              border: `1px solid ${alpha(domainColor, 0.3)}`,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '22px',
            }}
          />
        </Box> */}

        {/* Image for Custom type */}
        {enhancedPublication.type === 'Custom' && enhancedPublication.image_url && (
          <Box
            sx={{
              width: '100%',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 2,
              mt: 2,
            }}
          >
            <img
              src={enhancedPublication.image_url}
              alt={enhancedPublication.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover',
              }}
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
          </Box>
        )}

        {/* Text Content */}
        {enhancedPublication.text1 && (
          <Typography
            variant="body2"
            sx={{
              color: alpha('#E0E0E0', 0.8),
              fontSize: '0.875rem',
              lineHeight: 1.6,
              mb: (enhancedPublication.text2 || (enhancedPublication.text1 && enhancedPublication.text1.length > 150)) ? 1 : 2,
              display: '-webkit-box',
              WebkitLineClamp: expanded ? 'unset' : 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {enhancedPublication.text1}
          </Typography>
        )}

        {/* Expandable Text */}
        {enhancedPublication.text2 && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Typography
              variant="body2"
              sx={{
                color: alpha('#E0E0E0', 0.7),
                fontSize: '0.825rem',
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              {enhancedPublication.text2}
            </Typography>
          </Collapse>
        )}

        {/* Chart */}
        {enhancedPublication.graph_config && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <SmartCardChart config={enhancedPublication.graph_config} color={domainColor} />
          </Box>
        )}

        {/* Expand Button - Show if text2 exists OR if text1 is long enough to be truncated */}
        {(enhancedPublication.text2 || (enhancedPublication.text1 && enhancedPublication.text1.length > 150)) && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              />
            }
            sx={{
              color: domainColor,
              textTransform: 'none',
              fontSize: '0.75rem',
              p: 0,
              minWidth: 'auto',
              mb: 2,
              '&:hover': {
                bgcolor: 'transparent',
                color: alpha(domainColor, 0.8),
              },
            }}
          >
            {expanded ? 'moins' : 'plus'}
          </Button>
        )}

        {/* Action button only when there is a valid target */}
        {hasActionButton && (
          <Button
            variant="contained"
            onClick={handleAction}
            fullWidth
            sx={{
              mt: 2,
              background: typeGradient,
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              py: 1.25,
              fontSize: '0.875rem',
              boxShadow: `0 4px 12px ${alpha(domainColor, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 16px ${alpha(domainColor, 0.4)}`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            {enhancedPublication.button_text || getTranslation('viewAction', lang) /* LAP-733 (GE022) round 10 */}
          </Button>
        )}
      </CardContent>

      {/* Footer Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          bgcolor: 'rgba(0, 0, 0, 0.2)',
        }}
      >
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={handleLike}
            sx={{
              color: enhancedPublication.was_liked ? '#ef4444' : 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                color: '#ef4444',
                bgcolor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            {enhancedPublication.was_liked ? (
              <FavoriteIcon fontSize="small" />
            ) : (
              <FavoriteBorderIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleSave}
            sx={{
              color: enhancedPublication.is_saved ? domainColor : 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                color: domainColor,
                bgcolor: alpha(domainColor, 0.1),
              },
            }}
          >
            {enhancedPublication.is_saved ? (
              <BookmarkIcon fontSize="small" />
            ) : (
              <BookmarkBorderIcon fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={typeLabel /* LAP-733 (GE022) round 14 */}
            size="small"
            sx={{
              background: typeGradient,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />

          {/* Show saved/liked indicators in archived view */}
          {showArchivedIndicators && (
            <>
              {enhancedPublication.is_saved && (
                <Chip
                  icon={<BookmarkIcon sx={{ fontSize: '0.7rem !important', color: '#8b5cf6 !important' }} />}
                  label="Saved"
                  size="small"
                  sx={{
                    bgcolor: alpha('#8b5cf6', 0.2),
                    color: '#8b5cf6',
                    fontSize: '0.65rem',
                    height: '18px',
                    '& .MuiChip-icon': {
                      marginLeft: '4px',
                    },
                    '& .MuiChip-label': {
                      px: 0.5,
                    },
                  }}
                />
              )}
              {enhancedPublication.was_liked && (
                <Chip
                  icon={<FavoriteIcon sx={{ fontSize: '0.7rem !important', color: '#ef4444 !important' }} />}
                  label="Liked"
                  size="small"
                  sx={{
                    bgcolor: alpha('#ef4444', 0.2),
                    color: '#ef4444',
                    fontSize: '0.65rem',
                    height: '18px',
                    '& .MuiChip-icon': {
                      marginLeft: '4px',
                    },
                    '& .MuiChip-label': {
                      px: 0.5,
                    },
                  }}
                />
              )}
            </>
          )}

          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '0.7rem',
            }}
          >
            {new Date(enhancedPublication.datetime_displayed).toLocaleDateString(dateLocale, { /* LAP-733 (GE022) round 10 */
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        </Box>
      </Box>
    </Card>
  )
};

export default SmartCard;